import { useMemo, useRef, useState } from 'react';
import {
  searchCities,
  type BirthTimeInput,
  type CityEntry,
  type LocaleId,
  type UserDoc,
} from '@astrally/shared';
import { useApp, type StoredBirthProfile } from '../../state/AppState';
import { useI18n } from '../../i18n';
import { AstrallyMark } from '../../components/AstrallyMark';

/**
 * Onboarding: welcome → identity → birthplace → birth-time status (3 modes,
 * none forcing an invented time) → language/city → intents + age confirm →
 * matching consent → chart calculation.
 */

type Step =
  | 'welcome'
  | 'account'
  | 'identity'
  | 'place'
  | 'time'
  | 'context'
  | 'confirm'
  | 'calc';

const BROAD_WINDOWS = {
  morning: { start: { hour: 5, minute: 0 }, end: { hour: 11, minute: 0 } },
  afternoon: { start: { hour: 11, minute: 0 }, end: { hour: 17, minute: 0 } },
  evening: { start: { hour: 17, minute: 0 }, end: { hour: 22, minute: 0 } },
  night: { start: { hour: 22, minute: 0 }, end: { hour: 5, minute: 0 } },
} as const;

/** Turn Firebase auth error codes into something a person can act on. */
function friendlyAuthError(e: unknown): string {
  const msg = String(e);
  if (msg.includes('email-already-in-use'))
    return 'That email already has an account — switch to "I have one" and sign in.';
  if (msg.includes('invalid-credential') || msg.includes('wrong-password'))
    return 'That email and password did not match. Check them and try again.';
  if (msg.includes('user-not-found'))
    return 'No account with that email yet — switch to "New account".';
  if (msg.includes('weak-password')) return 'Please choose a password of at least 6 characters.';
  if (msg.includes('invalid-email')) return 'That email address does not look valid.';
  if (msg.includes('network')) return 'Network problem reaching Astrally. Check your connection.';
  // Never show a raw "FirebaseError: [code=...]" — it tells the user nothing
  // they can act on and reads as a crash.
  if (msg.includes('permission-denied'))
    return 'Your account signed in, but saving your profile was refused. Please try again — if it keeps happening, tell us and we will look.';
  return msg.replace(/^(FirebaseError|Error):\s*/, '');
}

export function OnboardingFlow() {
  const { t } = useI18n();
  const { completeOnboarding, signInExisting, backend } = useApp();
  const [step, setStep] = useState<Step>('welcome');
  const [error, setError] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  /** Set once a returning account has actually authenticated. */
  const [returning, setReturning] = useState(false);

  // Firebase mode needs a real account; demo mode uses a local session.
  const needsAccount = backend.kind === 'firebase';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [authMode, setAuthMode] = useState<'signup' | 'signin'>('signup');

  const [name, setName] = useState('');
  // Empty on purpose. A placeholder date here reads as data the app fetched —
  // a returning user saw "15-03-1995" and reasonably concluded sign-in had
  // pulled the wrong birth date. An empty field cannot be mistaken for a
  // wrong answer, and the Continue gate already requires a value.
  const [dob, setDob] = useState('');
  const [cityQuery, setCityQuery] = useState('');
  const [city, setCity] = useState<CityEntry | null>(null);
  const [manual, setManual] = useState(false);
  const [manualLat, setManualLat] = useState('');
  const [manualLon, setManualLon] = useState('');
  const [manualTz, setManualTz] = useState('Asia/Kolkata');

  const [timeMode, setTimeMode] = useState<'exact' | 'approximate' | 'unknown'>('exact');
  const [exactTime, setExactTime] = useState('14:30');
  const [broad, setBroad] = useState<keyof typeof BROAD_WINDOWS | 'custom'>('morning');
  const [customStart, setCustomStart] = useState('09:00');
  const [customEnd, setCustomEnd] = useState('12:00');

  const [locale, setLocaleChoice] = useState<LocaleId>('en');
  const [currentCity, setCurrentCity] = useState('Mumbai');
  const [interests, setInterests] = useState('technology, music');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [calculating, setCalculating] = useState(false);

  const emailRef = useRef<HTMLLabelElement>(null);
  const passwordRef = useRef<HTMLLabelElement>(null);
  const nameRef = useRef<HTMLLabelElement>(null);
  const dobRef = useRef<HTMLLabelElement>(null);
  const cityRef = useRef<HTMLLabelElement>(null);

  const cityResults = useMemo(() => searchCities(cityQuery), [cityQuery]);

  // What each gated step is still waiting for. A one-character name is not a
  // name: it produced "Good evening, A" on the home screen.
  const needsEmail = !email.includes('@');
  const needsPassword = password.length < 6;
  const needsName = name.trim().length < 2;
  const needsDob = !dob;
  const needsCity = !manual && !city;

  /** Every step change starts clean — an error from a step you left is noise. */
  const go = (next: Step) => {
    setError('');
    setStep(next);
  };

  /**
   * Refuse to advance, and say why.
   *
   * These CTAs used to be `disabled`, so a tap produced nothing at all — no
   * event, no message, no clue. The rules they enforced were invisible or lived
   * in a placeholder that vanishes on the first keystroke ("At least 6
   * characters"), and the birthplace field could look filled while no city had
   * actually been picked. A primary action must always answer a tap, so an
   * unfinished step now names what is missing and scrolls to it. Same contract
   * as SocialOptInScreen.
   */
  const block = (message: string, field: HTMLElement | null) => {
    setError(message);
    // Guarded: jsdom has no scrollIntoView, and it is a nicety rather than part
    // of the contract — the message is what actually unblocks someone.
    field?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  };

  const parseHM = (s: string) => {
    const [h, m] = s.split(':').map(Number);
    return { hour: h || 0, minute: m || 0 };
  };

  const buildTimeInput = (): BirthTimeInput => {
    if (timeMode === 'exact') return { kind: 'exact', time: parseHM(exactTime) };
    if (timeMode === 'unknown') return { kind: 'unknown' };
    if (broad === 'custom') {
      return {
        kind: 'approximate',
        window: { start: parseHM(customStart), end: parseHM(customEnd) },
        label: 'custom',
      };
    }
    return { kind: 'approximate', window: BROAD_WINDOWS[broad], label: broad };
  };

  const finish = async () => {
    setError('');
    const [y, m, d] = dob.split('-').map(Number);
    const place = manual
      ? {
          lat: Number(manualLat),
          lon: Number(manualLon),
          timezone: manualTz,
          label: 'Custom location',
        }
      : city
        ? { lat: city.lat, lon: city.lon, timezone: city.timezone, label: city.name, countryCode: city.countryCode }
        : null;
    if (!place || Number.isNaN(place.lat) || Number.isNaN(place.lon)) {
      setError('Please choose a birthplace.');
      setStep('place');
      return;
    }
    const profile: StoredBirthProfile = {
      displayName: name.trim() || 'Friend',
      input: { date: { year: y, month: m, day: d }, timeInput: buildTimeInput(), place },
    };
    const now = new Date().toISOString();
    const user: UserDoc = {
      uid: '',
      displayName: profile.displayName,
      ageBand: '25-34',
      region: 'in-mh-mumbai',
      city: currentCity || undefined,
      languages: [locale],
      // Intentions are collected only when the user opts into the social
      // layer — a private account has nothing to match on.
      intents: [],
      interests: interests
        .split(',')
        .map((s) => s.trim().toLowerCase())
        .filter(Boolean)
        .slice(0, 10),
      lifeStage: 'early-career',
      // PRIVATE BY DEFAULT: no social layer until the user explicitly opts in
      // (Profile → Connections). Intentions are collected there, not here.
      socialEnabled: false,
      discoverable: false,
      recommendationsPaused: false,
      ageConfirmed,
      createdAtISO: now,
      lastActiveAtISO: now,
    };
    setCalculating(true);
    setStep('calc');
    try {
      await completeOnboarding(
        profile,
        user,
        locale,
        needsAccount ? { email: email.trim(), password, mode: authMode } : undefined,
      );
    } catch (e) {
      setCalculating(false);
      setError(friendlyAuthError(e));
      setStep(needsAccount ? 'account' : 'confirm');
    }
  };

  return (
    // No tab bar on this flow, so nothing else keeps the CTA clear of the
    // gesture pill — and this is the first screen every new user sees.
    <div className="screen fade-in" style={{ paddingBottom: 'calc(24px + var(--safe-bottom))' }}>
      {step === 'welcome' && (
        <div className="col" style={{ gap: 14, marginTop: '5vh', textAlign: 'center' }}>
          <div className="row" style={{ justifyContent: 'center' }}>
            <AstrallyMark size={64} />
          </div>
          <div className="h1">{t('app.name')}</div>
          <div className="muted small">{t('app.subtitle')}</div>

          {/* What you always get. */}
          <div className="card" style={{ textAlign: 'left' }}>
            <div className="h3" style={{ marginBottom: 6 }}>{t('onb.welcome.title')}</div>
            <div className="muted small" style={{ lineHeight: 1.55 }}>
              {t('onb.welcome.body')}
            </div>
          </div>

          {/* The social layer, stated as the opt-in it actually is. */}
          <div className="card" style={{ textAlign: 'left' }}>
            <div className="row" style={{ marginBottom: 8 }}>
              <span className="chip chip--fact" style={{ cursor: 'default' }}>
                🔒 {t('onb.welcome.optionalBadge')}
              </span>
            </div>
            <div className="h3" style={{ marginBottom: 6 }}>{t('onb.welcome.optionalTitle')}</div>
            <div className="muted small" style={{ lineHeight: 1.55 }}>
              {t('onb.welcome.optionalBody')}
            </div>
          </div>

          <div className="tiny gold">{t('onb.welcome.noFeed')}</div>

          <button
            className="btn btn--primary btn--block"
            onClick={() => {
              setAuthMode('signup');
              go(needsAccount ? 'account' : 'identity');
            }}
          >
            {t('onb.start')}
          </button>
          {needsAccount && (
            // Returning users had no way in except the "Get started" button,
            // so signing in was buried inside a flow that reads as registering.
            <button
              className="btn btn--ghost btn--block"
              onClick={() => {
                setAuthMode('signin');
                go('account');
              }}
            >
              {t('onb.haveAccount')}
            </button>
          )}
          <div className="tiny faint">{t('app.tagline')}</div>
        </div>
      )}

      {step === 'account' && (
        <div className="col" style={{ gap: 14 }}>
          <div className="h1">{authMode === 'signup' ? 'Create your account' : 'Welcome back'}</div>
          <div className="muted small">
            Your account secures your profile and connections. Your birth details stay on this
            device — only derived kundli features sync.
          </div>
          <div className="seg">
            <button
              className={`seg__opt ${authMode === 'signup' ? 'seg__opt--on' : ''}`}
              onClick={() => setAuthMode('signup')}
            >
              New account
            </button>
            <button
              className={`seg__opt ${authMode === 'signin' ? 'seg__opt--on' : ''}`}
              onClick={() => setAuthMode('signin')}
            >
              I have one
            </button>
          </div>
          <label className="field" ref={emailRef}>
            Email
            <input
              className="input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => {
                setError('');
                setEmail(e.target.value);
              }}
              placeholder="you@example.com"
            />
          </label>
          <label className="field" ref={passwordRef}>
            Password
            <input
              className="input"
              type="password"
              autoComplete={authMode === 'signup' ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => {
                setError('');
                setPassword(e.target.value);
              }}
              placeholder="At least 6 characters"
            />
          </label>
          {error && <div className="banner banner--danger">{error}</div>}
          <div className="row">
            <button className="btn btn--ghost" onClick={() => go('welcome')}>
              {t('common.back')}
            </button>
            <button
              className={`btn btn--primary ${needsEmail || needsPassword ? 'btn--incomplete' : ''}`}
              style={{ flex: 1 }}
              disabled={authBusy}
              onClick={async () => {
                if (needsEmail || needsPassword) {
                  block(
                    needsEmail ? t('form.needEmail') : t('form.needPassword'),
                    needsEmail ? emailRef.current : passwordRef.current,
                  );
                  return;
                }
                // Signing in happens HERE, not on the last screen. A wrong
                // password used to surface only after five more screens of
                // typing, which is also why signing in felt like registering.
                if (authMode === 'signin') {
                  setAuthBusy(true);
                  setError('');
                  try {
                    const existing = await signInExisting(email.trim(), password);
                    setReturning(true);
                    if (existing?.displayName) setName(existing.displayName);
                  } catch (e) {
                    setError(friendlyAuthError(e));
                    return;
                  } finally {
                    setAuthBusy(false);
                  }
                }
                go('identity');
              }}
            >
              {authBusy ? <span className="spinner" /> : t('common.continue')}
            </button>
          </div>
        </div>
      )}

      {step === 'identity' && (
        <div className="col" style={{ gap: 16 }}>
          <div className="h1">{returning ? t('onb.restore.title') : t('onb.name')}</div>
          {returning && (
            // Say why a signed-in user is being asked for this again, instead
            // of letting it look like the app forgot them.
            <div className="banner banner--info small">{t('onb.restore.body')}</div>
          )}
          <label className="field" ref={nameRef}>
            {t('onb.name.ph')}
            <input
              className="input"
              placeholder={t('onb.name.ph')}
              value={name}
              onChange={(e) => {
                setError('');
                setName(e.target.value);
              }}
              maxLength={40}
            />
          </label>
          <label className="field" ref={dobRef}>
            {t('onb.dob')}
            <input
              className="input"
              type="date"
              value={dob}
              onChange={(e) => {
                setError('');
                setDob(e.target.value);
              }}
              max={new Date().toISOString().slice(0, 10)}
            />
          </label>
          {error && <div className="banner banner--danger">{error}</div>}
          <button
            className={`btn btn--primary btn--block ${needsName || needsDob ? 'btn--incomplete' : ''}`}
            onClick={() => {
              if (needsName || needsDob) {
                block(
                  needsName ? t('form.needName') : 'Please add your date of birth.',
                  needsName ? nameRef.current : dobRef.current,
                );
                return;
              }
              go('place');
            }}
          >
            {t('common.continue')}
          </button>
        </div>
      )}

      {step === 'place' && (
        <div className="col" style={{ gap: 14 }}>
          <div className="h1">{t('onb.place')}</div>
          {!manual && (
            <>
              <label className="field" ref={cityRef}>
                {t('onb.place.search')}
                <input
                  className="input"
                  placeholder={t('onb.place.search')}
                  value={cityQuery}
                  onChange={(e) => {
                    setError('');
                    setCityQuery(e.target.value);
                    // Typing again invalidates an earlier pick, otherwise the
                    // field can read "Delhi" while the stored birthplace is
                    // still Mumbai.
                    setCity(null);
                  }}
                />
              </label>
              <div className="col" style={{ gap: 6 }}>
                {cityResults.map((c) => (
                  <button
                    key={`${c.name}-${c.countryCode}`}
                    className={`btn btn--ghost ${city?.name === c.name ? 'chip--on' : ''}`}
                    style={{ justifyContent: 'space-between' }}
                    onClick={() => {
                      setError('');
                      setCity(c);
                      setCityQuery(`${c.name}, ${c.country}`);
                    }}
                  >
                    <span>{c.name}{c.admin ? `, ${c.admin}` : ''}</span>
                    <span className="faint small">{c.country}</span>
                  </button>
                ))}
              </div>
              <button
                className="btn btn--ghost btn--small"
                onClick={() => {
                  setError('');
                  setManual(true);
                }}
              >
                {t('onb.place.manual')}
              </button>
            </>
          )}
          {manual && (
            <div className="col">
              <label className="field">
                Latitude
                <input className="input" value={manualLat} onChange={(e) => setManualLat(e.target.value)} placeholder="19.0760" />
              </label>
              <label className="field">
                Longitude
                <input className="input" value={manualLon} onChange={(e) => setManualLon(e.target.value)} placeholder="72.8777" />
              </label>
              <label className="field">
                IANA timezone
                <input className="input" value={manualTz} onChange={(e) => setManualTz(e.target.value)} placeholder="Asia/Kolkata" />
              </label>
              <button
                className="btn btn--ghost btn--small"
                onClick={() => {
                  setError('');
                  setManual(false);
                }}
              >
                ← Search cities instead
              </button>
            </div>
          )}
          {error && <div className="banner banner--danger">{error}</div>}
          <div className="row">
            <button className="btn btn--ghost" onClick={() => go('identity')}>{t('common.back')}</button>
            <button
              className={`btn btn--primary ${needsCity ? 'btn--incomplete' : ''}`}
              style={{ flex: 1 }}
              onClick={() => {
                if (needsCity) {
                  block(t('form.needCity'), cityRef.current);
                  return;
                }
                go('time');
              }}
            >
              {t('common.continue')}
            </button>
          </div>
        </div>
      )}

      {step === 'time' && (
        <div className="col" style={{ gap: 14 }}>
          <div className="h1">{t('onb.timeStatus')}</div>
          {(
            [
              ['exact', t('onb.time.exact'), t('onb.time.exact.note')],
              ['approximate', t('onb.time.approx'), t('onb.time.approx.note')],
              ['unknown', t('onb.time.unknown'), t('onb.time.unknown.note')],
            ] as const
          ).map(([mode, label, note]) => (
            <div
              key={mode}
              className={`card ${timeMode === mode ? 'card--strong' : ''}`}
              style={{ cursor: 'pointer', borderColor: timeMode === mode ? 'var(--accent)' : undefined }}
              onClick={() => setTimeMode(mode)}
            >
              <div className="h3">{label}</div>
              <div className="muted small" style={{ marginTop: 6, lineHeight: 1.5 }}>{note}</div>
              {timeMode === 'exact' && mode === 'exact' && (
                <input
                  className="input"
                  type="time"
                  style={{ marginTop: 10 }}
                  value={exactTime}
                  onChange={(e) => setExactTime(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              {timeMode === 'approximate' && mode === 'approximate' && (
                <div className="col" style={{ marginTop: 10 }} onClick={(e) => e.stopPropagation()}>
                  <select
                    className="select"
                    value={broad}
                    onChange={(e) => setBroad(e.target.value as typeof broad)}
                  >
                    <option value="morning">{t('onb.window.morning')}</option>
                    <option value="afternoon">{t('onb.window.afternoon')}</option>
                    <option value="evening">{t('onb.window.evening')}</option>
                    <option value="night">{t('onb.window.night')}</option>
                    <option value="custom">{t('onb.window.custom')}</option>
                  </select>
                  {broad === 'custom' && (
                    <div className="row">
                      <input className="input" type="time" value={customStart} onChange={(e) => setCustomStart(e.target.value)} />
                      <span className="muted">→</span>
                      <input className="input" type="time" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
          <div className="row">
            <button className="btn btn--ghost" onClick={() => go('place')}>{t('common.back')}</button>
            <button className="btn btn--primary" style={{ flex: 1 }} onClick={() => go('context')}>
              {t('common.continue')}
            </button>
          </div>
        </div>
      )}

      {step === 'context' && (
        <div className="col" style={{ gap: 14 }}>
          <div className="h1">{t('onb.language')}</div>
          <div className="seg">
            {(['en', 'hi', 'hinglish', 'bn'] as LocaleId[]).map((l) => (
              <button
                key={l}
                className={`seg__opt ${locale === l ? 'seg__opt--on' : ''}`}
                onClick={() => setLocaleChoice(l)}
              >
                {{ en: 'English', hi: 'हिन्दी', hinglish: 'Hinglish', bn: 'বাংলা' }[l]}
              </button>
            ))}
          </div>
          <label className="field">
            {t('onb.city')}
            <input className="input" value={currentCity} onChange={(e) => setCurrentCity(e.target.value)} />
          </label>
          <label className="field">
            Interests (comma-separated)
            <input className="input" value={interests} onChange={(e) => setInterests(e.target.value)} />
          </label>
          <div className="row">
            <button className="btn btn--ghost" onClick={() => go('time')}>{t('common.back')}</button>
            <button className="btn btn--primary" style={{ flex: 1 }} onClick={() => go('confirm')}>
              {t('common.continue')}
            </button>
          </div>
        </div>
      )}

      {step === 'confirm' && (
        <div className="col" style={{ gap: 14 }}>
          <div className="h1">{t('onb.private.title')}</div>
          <div className="card" style={{ lineHeight: 1.6 }}>
            <div className="row" style={{ marginBottom: 8 }}>
              <span className="chip chip--fact" style={{ cursor: 'default' }}>
                🔒 {t('social.private.badge')}
              </span>
            </div>
            <div className="muted small">{t('onb.private.body')}</div>
          </div>

          <div className="card">
            <label className="row" style={{ cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
              />
              <span>{t('onb.ageConfirm')}</span>
            </label>
            <div className="muted small" style={{ marginTop: 6 }}>{t('onb.ageConfirm.why')}</div>
          </div>

          <div className="banner banner--gold small">{t('safety.disclaimer')}</div>
          {error && <div className="banner banner--danger">{error}</div>}
          <div className="row">
            <button className="btn btn--ghost" onClick={() => go('context')}>{t('common.back')}</button>
            <button
              className="btn btn--primary"
              style={{ flex: 1 }}
              disabled={!ageConfirmed}
              onClick={finish}
            >
              {t('onb.finish')}
            </button>
          </div>
        </div>
      )}

      {step === 'calc' && (
        <div className="col" style={{ marginTop: '30vh', alignItems: 'center', gap: 16 }}>
          <div className="spinner" />
          <div className="muted">{calculating ? t('onb.calculating') : t('common.done')}</div>
        </div>
      )}
    </div>
  );
}
